<script setup lang="ts">
  import type { IntroTextBlockStoryblok } from '@/storyblok/component-types-sb'
  import { cva } from 'class-variance-authority'

  const props = withDefaults(
    defineProps<{
      blok: IntroTextBlockStoryblok
      isNested: boolean
    }>(),
    {
      isNested: false
    }
  )

  const sectionCva = computed(() =>
    cva('md:mx-auto md:w-10/12 lg:w-full', {
      variants: {
        withoutBottomMargin: {
          true: 'sm:-mb-6',
          false: ''
        }
      }
    })({
      withoutBottomMargin: props.blok.withoutBottomMargin
    })
  )

  const headingCva = computed(() =>
    cva('', {
      variants: {
        textSize: {
          small: '',
          medium: 'mb-4 lg:mb-6',
          large: 'sm:mb-4',
          tiny: 'text-h3'
        },
        withoutTopMargin: {
          true: 'md:mb-6',
          false: 'my-6 sm:my-4'
        },
        titleOnly: {
          true: 'mb-0',
          false: ''
        }
      }
    })({
      textSize: props.blok.textSize,
      withoutTopMargin: props.blok.withoutTopMargin,
      titleOnly: !props.blok.descriptionContent?.length
    })
  )

  const descriptionCva = computed(() =>
    cva('font-light lg:mb-6', {
      variants: {
        textSize: {
          small: 'text-body-2',
          large: 'text-lead md:lead-desktop text-lg',
          medium: 'text-body-1 md:text-body-large',
          tiny: ''
        },
        textSpacing: {
          compact: 'text-body-1 sm:text-body-2 md:text-base md:leading-7',
          normal: 'text-secondary'
        }
      }
    })({
      textSize: props.blok.textSize,
      textSpacing: props.blok.textSpacing
    })
  )

  const contentCva = computed(() =>
    cva('sm:gap-y-2 lg:gap-y-6', {
      variants: {
        textPositionMobile: {
          left: 'text-left',
          center: 'text-center',
          right: 'text-right'
        },
        textPositionTablet: {
          left: 'sm:text-left',
          center: 'sm:text-center',
          right: 'sm:text-right'
        },
        textPositionDesktop: {
          left: 'md:text-left',
          center: 'md:text-center',
          right: 'md:text-right'
        }
      }
    })({
      textPositionMobile: props.blok.textPositionMobile,
      textPositionTablet: props.blok.textPositionTablet,
      textPositionDesktop: props.blok.textPositionDesktop
    })
  )

  const layoutJustifyCva = computed(() =>
    cva('flex', {
      variants: {
        positionOnDesktop: {
          left: 'md:justify-start',
          center: 'md:justify-center',
          right: 'md:justify-end'
        },
        contentWidth: {
          full: '',
          narrow: 'md:w-9/12 lg:w-10/12 md:mx-auto'
        }
      }
    })({
      positionOnDesktop: props.blok.positionOnDesktop,
      contentWidth: props.blok.contentWidth
    })
  )

  const widthOnDesktop = computed(() => {
    if (props.isNested && props.blok.textWidth === 'full') {
      return 'nestedFull'
    }

    return props.blok.textWidth
  })

  const containerWidthCva = computed(() =>
    cva('flex', {
      variants: {
        widthOnDesktop: {
          full: 'md:w-10/12 lg:w-8/12',
          nestedFull: 'w-full',
          wide: '',
          half: 'sm:w-7/12 md:w-1/2 sm:mx-auto md:mx-0',
          narrow: 'sm:w-7/12 lg:w-5/12 sm:mx-auto md:mx-0'
        }
      }
    })({
      widthOnDesktop: widthOnDesktop.value
    })
  )

  const headingTag = computed(() => (props.blok.textSize === 'large' ? 'h1' : 'h2'))
  const descriptionContentBlock = computed(() => props.blok.descriptionContent?.[0])
</script>

<template>
  <section :class="sectionCva">
    <div :class="layoutJustifyCva">
      <div :class="containerWidthCva">
        <div :class="contentCva">
          <component
            :is="headingTag"
            :class="headingCva"
          >
            {{ blok.title }}
          </component>

          <div
            v-if="descriptionContentBlock"
            :class="descriptionCva"
          >
            <StoryblokComponent :blok="descriptionContentBlock" />
          </div>

          <div
            v-if="blok.link"
            class="sm:mt-3"
          >
            <div
              v-for="link in blok.link"
              :key="link._uid"
            >
              <LazyNuxtLink
                v-if="link.component === 'link'"
                :to="link.href.cached_url"
                class="text-bt-blue-ui underline"
              >
                {{ link.text ?? link.story?.name }}
              </LazyNuxtLink>
              <LazyButtonLink
                v-else
                :blok="link"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
